import type { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import type { UserEligibility } from '@wizbii/utils/models';

import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { Store } from '@ngrx/store';
import { eligibility } from '@stores/banking/banking-ngrx.selector';
import { userId } from '@wizbii-utils/angular/stores';
import { FibiiOrdersWebservice } from '@wizbii-utils/angular/webservices';
import { OrderStatusEnum, orderStatusSuccession } from '@wizbii/utils/models';
import { catchError, filter, map, switchMap, tap } from 'rxjs';

export const orderInProgressGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const store = inject(Store);
  const fibiiOrdersWebservice = inject(FibiiOrdersWebservice);
  const stepId = route.paramMap.get('stepId');

  return store.select(userId).pipe(
    filter((userId): userId is string => !!userId),
    switchMap((userId) => fibiiOrdersWebservice.getLastOrder(userId, environment.i18n.country)),
    switchMap((lastOrder) => {
      if (stepId && lastOrder.status && stepId.toUpperCase() !== lastOrder.status.toUpperCase()) {
        return router.navigate(['/', FeaturesRoutingEnum.Order, lastOrder.status.toLowerCase()]);
      }

      if (orderStatusSuccession[lastOrder.status] >= orderStatusSuccession[OrderStatusEnum.PROCESSING]) {
        return router.navigate(['/', FeaturesRoutingEnum.Dashboard]);
      }

      return fibiiOrdersWebservice
        .getSteps(lastOrder.id)
        .pipe(tap((steps) => (route.data = { ...route.data, steps: steps.steps })));
    }),
    catchError(() => {
      return router.navigate(['/', FeaturesRoutingEnum.Promise]);
    }),
    map(() => true)
  );
};

export const isEligibleGuard: CanActivateFn = () => {
  const router = inject(Router);
  const store = inject(Store);
  return store.select(eligibility).pipe(
    filter((eligibility): eligibility is UserEligibility => !!eligibility),
    map((eligibility) => {
      if (!eligibility.isEligible) {
        return router.navigate(['/', FeaturesRoutingEnum.Dashboard]);
      }
      return eligibility.isEligible;
    }),
    map(() => true)
  );
};
